import type { NewRelicPrimitive } from '../types';

/**
 * Convert any error value to an Error object
 * @param error Error object, string, or any value
 * @returns Error object
 */
export function formatErrorToException(error: unknown): Error {
  if (error instanceof Error) {
    return error;
  }

  if (typeof error === 'string') {
    return new Error(error);
  }

  if (typeof error === 'object' && error !== null) {
    try {
      return new Error(JSON.stringify(error));
    } catch {
      return new Error(String(error));
    }
  }

  return new Error(String(error));
}

/**
 * Format and merge context objects into NewRelic-compatible attributes
 * Filters out undefined/null values and ensures primitive types
 * @param contexts Context objects to merge (later objects override earlier ones)
 * @returns Formatted attributes object
 */
export function formatContextAttributes(
  ...contexts: (Record<string, unknown> | undefined)[]
): Record<string, NewRelicPrimitive> {
  const merged: Record<string, unknown> = {};

  for (const context of contexts) {
    if (context) {
      Object.assign(merged, context);
    }
  }

  const formatted: Record<string, NewRelicPrimitive> = {};

  for (const [key, value] of Object.entries(merged)) {
    if (value !== undefined && value !== null) {
      if (typeof value === 'string' || typeof value === 'number') {
        formatted[key] = value;
      } else {
        formatted[key] = String(value);
      }
    }
  }

  return formatted;
}
